import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import type {
  BulkAction,
  Folder,
  MailAddress,
  MessageDetail,
  MessageSummary,
  NewMailEvent,
  OutgoingAttachment,
  SendRequest,
  SnoozedMessage,
} from 'zitimail-shared-types';
import {
  canChangeFolder,
  canHoldFolders,
  FOLDER_SEPARATOR,
  folderDepth,
  folderLabel,
  folderMoveTargets,
  movedFolderPath,
  newFolderPath,
  quotaLevel,
  quotaRatio,
  renamedFolderPath,
  type QuotaUsage,
} from 'zitimail-shared-types';

import { allowScheduling, mail, MailError, type ScheduledMessage } from '../api/mail';
import { notifyNewMail } from './notify';
import { whenLabel } from './Snooze';
import {
  ArchiveIcon,
  ClockIcon,
  CloseIcon,
  EnvelopeOpenIcon,
  FlagIcon,
  FolderMoveIcon,
  InboxIcon,
  PenIcon,
  PlusIcon,
  RefreshIcon,
  SearchIcon,
  SendIcon,
  ShieldIcon,
  TrashIcon,
  MenuIcon,
} from '../components/icons';
import { BuildInfo } from '../components/BuildInfo';
import { ContextMenu, menuAtElement, type MenuAt, type MenuItem } from '../components/ContextMenu';
import { useToast } from '../components/Toasts';
import { Compose, draftFiles, signatureHtml, textToHtml, type Draft } from './Compose';
import { conversations, displayName, fullDate, type Conversation } from './format';
import { ListSkeleton, MESSAGES_DRAG, MessageList, rowKey, type RowAction } from './MessageList';
import { Reader, type ConversationAction, type ReplyMode } from './Reader';
import { useLayout } from './layout';
import { Shortcuts } from './Shortcuts';
import { Splitter } from './Splitter';
import { rememberRecipients } from './recentPeople';
import { undoSeconds } from './undoSend';

const FOLDER_ICON: Record<string, (p: { className?: string }) => JSX.Element> = {
  '\\Inbox': InboxIcon,
  '\\Sent': SendIcon,
  '\\Drafts': PenIcon,
  '\\Archive': ArchiveIcon,
  '\\Junk': ShieldIcon,
  '\\Trash': TrashIcon,
};

const THREADS_KEY = 'genworx.mail.threads';

function readThreads(): boolean {
  try {
    return window.localStorage.getItem(THREADS_KEY) !== 'off';
  } catch {
    return true;
  }
}

/** True when a keystroke belongs to a text field rather than to the app. */
function typing(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return Boolean(
    el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)),
  );
}

/** What the writing assistant reads of a message: its text, kept short. */
function plainBody(detail: MessageDetail): string {
  const text =
    detail.text ??
    (detail.html
      ? (new DOMParser().parseFromString(detail.html, 'text/html').body.textContent ?? '')
      : '');
  return text
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .slice(0, 3000);
}

/** What attachments may add up to, as compose allows (Compose.tsx). */
const FORWARD_LIMIT = 25 * 1024 * 1024;

/**
 * A forwarded message's files, read from the server so they are sent again
 * with it; those past the 25 MB budget (or that cannot be read) are left out
 * and counted, for the person to be told.
 */
export async function forwardedFiles(
  detail: MessageDetail,
): Promise<{ files: Array<OutgoingAttachment & { size: number }>; left: number }> {
  const files: Array<OutgoingAttachment & { size: number }> = [];
  let total = 0;
  let left = 0;
  for (const a of detail.attachments) {
    if (total + a.sizeBytes > FORWARD_LIMIT) {
      left += 1;
      continue;
    }
    try {
      const response = await fetch(mail.attachmentUrl(detail.folder, detail.uid, a.part), {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(String(response.status));
      }
      const blob = await response.blob();
      if (total + blob.size > FORWARD_LIMIT) {
        left += 1;
        continue;
      }
      files.push({
        filename: a.filename,
        contentType: a.contentType || blob.type || 'application/octet-stream',
        data: await base64Of(blob),
        size: blob.size,
      });
      total += blob.size;
    } catch {
      left += 1;
    }
  }
  return { files, left };
}

function base64Of(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('could not read the file'));
    reader.readAsDataURL(blob);
  });
}

export function MailView({
  email,
  name,
  tenant,
  onSettings,
  onRecover,
  onMakeRule,
  writeTo,
}: {
  email: string;
  /** The person's display name, for the writing assistant's sign-off. */
  name?: string | undefined;
  /** The organisation's slug, for the writing assistant. */
  tenant?: string | undefined;
  onSettings: () => void;
  /** Opens the mail deleted for good that can still be recovered. */
  onRecover?: (() => void) | undefined;
  /** Opens Settings with a new rule for mail from this sender. */
  onMakeRule?: ((sender: MailAddress) => void) | undefined;
  /** Opens a new message to this address, e.g. from Contacts. */
  writeTo?: string | undefined;
}): JSX.Element {
  const toast = useToast();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [folder, setFolder] = useState('INBOX');
  const [messages, setMessages] = useState<MessageSummary[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [threads, setThreads] = useState(readThreads);
  const [openKey, setOpenKey] = useState<string | null>(null);
  // On a phone the folders are a drawer over the list.
  const [foldersOpen, setFoldersOpen] = useState(false);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [anchor, setAnchor] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [quota, setQuota] = useState<QuotaUsage | null>(null);
  const [help, setHelp] = useState(false);
  const [signature, setSignature] = useState<string | undefined>();
  const [plainWriting, setPlainWriting] = useState(false);
  const pendingSend = useRef<number | null>(null);
  const [scheduled, setScheduled] = useState<readonly ScheduledMessage[]>([]);
  const [snoozed, setSnoozed] = useState<readonly SnoozedMessage[]>([]);
  const searchBox = useRef<HTMLInputElement>(null);

  const fail = (e: unknown) => setError(e instanceof Error ? e.message : 'Something went wrong.');

  const rows: Conversation[] = useMemo(
    () =>
      threads
        ? conversations(messages)
        : messages.map((m) => ({
            messages: [m],
            latest: m,
            unread: !m.seen,
            flagged: m.flagged,
            hasAttachments: m.hasAttachments,
            people: [m.from],
          })),
    [messages, threads],
  );
  const open = rows.find((r) => r.messages.some((m) => rowKey(m) === openKey)) ?? null;
  const byUse = useMemo(() => new Map(folders.map((f) => [f.specialUse ?? '', f.path])), [folders]);
  const current = folders.find((f) => f.path === folder);
  const layout = useLayout();
  // Right-click menus, the rows a drag carries and the folder under it, and
  // a folder action asked for from a menu (FolderTools opens on it).
  const [menu, setMenu] = useState<MenuAt | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  const dragged = useRef<readonly Conversation[]>([]);
  const [dropOn, setDropOn] = useState<string | null>(null);
  const [folderStart, setFolderStart] = useState<{
    path: string;
    mode: 'rename' | 'move';
    n: number;
  } | null>(null);
  // A shared mailbox's folder says whose it is (ADR-0047).
  const folderName = current
    ? current.shared
      ? `${current.name} of ${current.shared}`
      : current.name
    : folder;

  const loadFolders = useCallback(() => {
    mail.folders().then(setFolders).catch(fail);
  }, []);

  const loadMessages = useCallback(
    (more = false) => {
      setBusy(true);
      mail
        .messages(folder, more ? cursor : undefined, query)
        .then((page) => {
          setMessages((current) => (more ? [...current, ...page.messages] : [...page.messages]));
          setCursor(page.nextCursor);
          setTotal(page.total);
          setError(null);
        })
        .catch(fail)
        .finally(() => {
          setBusy(false);
          setLoaded(true);
        });
    },
    [folder, cursor, query],
  );

  useEffect(() => {
    loadFolders();
    mail
      .quota()
      .then(setQuota)
      .catch(() => undefined);
  }, [loadFolders]);

  useEffect(() => {
    setOpenKey(null);
    setSelected(new Set());
    setLoaded(false);
    loadMessages(false);
    // A new folder or search starts from the top; `loadMessages` itself is
    // not a trigger, or every page loaded would reload the first.
  }, [folder, query]);

  // The signature, from the user's mail settings on the server.
  useEffect(() => {
    mail
      .settings()
      .then((s) => {
        setSignature(s.signature);
        setPlainWriting(s.composeFormat === 'text');
      })
      .catch(() => undefined);
  }, []);

  // A message held for undo must not vanish with the tab.
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (pendingSend.current !== null) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  /**
   * Sends after a short hold, with Undo in the toast: the compose window
   * closes at once, and Undo reopens it exactly as it was.
   */
  const sendLater = (request: SendRequest, reopen: Draft) => {
    setDraft(null);
    // The person's own Undo window (account menu); 0 sends at once.
    const hold = undoSeconds();
    const timer = window.setTimeout(() => {
      pendingSend.current = null;
      mail
        .send(request)
        .then((result) => {
          rememberRecipients(tenant, request);
          toast(
            result?.queued
              ? 'No connection: it is in the outbox, and goes as soon as you are back online.'
              : 'Message sent.',
          );
          refresh.current();
        })
        .catch((e: unknown) => {
          toast(e instanceof Error ? `Not sent: ${e.message}` : 'Not sent.', { tone: 'error' });
          setDraft(reopen);
        });
    }, hold * 1000);
    pendingSend.current = timer;
    if (hold === 0) {
      toast('Sending…');
      return;
    }
    toast(`Sending in ${hold} seconds`, {
      durationMs: hold * 1000,
      action: {
        label: 'Undo',
        run: () => {
          window.clearTimeout(timer);
          pendingSend.current = null;
          setDraft(reopen);
        },
      },
    });
  };

  /**
   * Send later. The first time, the user allows scheduled sending in a small
   * window (a silent sign-in); after that it is one click.
   */
  const scheduleLater = async (request: SendRequest, at: Date, reopen: Draft) => {
    setDraft(null);
    const when = at.toLocaleString([], {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
    try {
      let job: { id: string } | undefined;
      try {
        job = await mail.schedule(request, at);
      } catch (e) {
        if (!(e instanceof MailError && e.status === 428)) {
          throw e;
        }
        toast('Allow scheduled sending in the window that opened.');
        if (!(await allowScheduling())) {
          toast('Scheduled sending was not allowed; the message is back in compose.', {
            tone: 'error',
          });
          setDraft(reopen);
          return;
        }
        job = await mail.schedule(request, at);
      }
      const id = job.id;
      rememberRecipients(tenant, request);
      toast(`Scheduled for ${when}.`, {
        action: {
          label: 'Undo',
          run: () =>
            void mail
              .cancelScheduled(id)
              .then((back) =>
                setDraft({ ...reopen, ...(back.draftUid ? { draftUid: back.draftUid } : {}) }),
              )
              .catch((err: unknown) =>
                toast(err instanceof Error ? err.message : 'Not cancelled.', { tone: 'error' }),
              ),
        },
      });
      refresh.current();
    } catch (e) {
      toast(e instanceof Error ? `Not scheduled: ${e.message}` : 'Not scheduled.', {
        tone: 'error',
      });
      setDraft(reopen);
    }
  };

  /**
   * Snoozes every message of the conversation in this folder. Needs the same
   * once-only delegation as send later: the server brings them back.
   */
  const snooze = async (row: Conversation, until: Date) => {
    const here = row.messages.filter((m) => m.folder === folder);
    const one = (uid: string) => mail.snooze(folder, uid, until);
    try {
      const [first, ...rest] = here;
      if (!first) {
        return;
      }
      try {
        await one(first.uid);
      } catch (e) {
        if (!(e instanceof MailError && e.status === 428)) {
          throw e;
        }
        toast('Allow webmail to bring it back while you are away, in the window that opened.');
        if (!(await allowScheduling())) {
          toast('Snooze was not allowed; the message stays here.', { tone: 'error' });
          return;
        }
        await one(first.uid);
      }
      for (const m of rest) {
        await one(m.uid);
      }
      toast(`Snoozed until ${whenLabel(until)}.`);
      setOpenKey(null);
      refresh.current();
    } catch (e) {
      toast(e instanceof Error ? `Not snoozed: ${e.message}` : 'Not snoozed.', { tone: 'error' });
      refresh.current();
    }
  };
  const isSnoozedFolder = folder === 'Snoozed';
  useEffect(() => {
    if (isSnoozedFolder) {
      mail
        .snoozed()
        .then((list) => setSnoozed(list.messages))
        .catch(() => setSnoozed([]));
    }
  }, [isSnoozedFolder, messages]);

  // In the Scheduled folder, each message shows when it will go.
  const isScheduledFolder = folder === 'Scheduled';
  useEffect(() => {
    if (isScheduledFolder) {
      mail
        .scheduled()
        .then((list) => setScheduled(list.messages))
        .catch(() => setScheduled([]));
    }
  }, [isScheduledFolder, messages]);

  useEffect(() => {
    if (writeTo) {
      setDraft({ to: writeTo, cc: '', subject: '', text: '' });
    }
  }, [writeTo]);

  // New mail without a reload: the server pushes an event the moment the
  // Inbox changes; a slow poll and a refresh on returning to the tab cover a
  // dropped stream. A ref keeps the handlers on the current folder and search.
  const refresh = useRef<() => void>(() => undefined);
  refresh.current = () => {
    loadFolders();
    if (!query) {
      loadMessages(false);
    }
  };
  useEffect(() => {
    const events = typeof EventSource === 'undefined' ? null : new EventSource('/api/mail/events');
    const onChange = () => refresh.current();
    const onMail = (e: MessageEvent<string>) => {
      onChange();
      try {
        const event = JSON.parse(e.data) as NewMailEvent;
        if (event.latest) {
          notifyNewMail(
            displayName(event.latest.from) || 'New mail',
            event.latest.subject || '(no subject)',
          );
        }
      } catch {
        // An event without a description still refreshed the list.
      }
    };
    events?.addEventListener('mail', onMail);
    events?.addEventListener('changed', onChange);
    const poll = window.setInterval(onChange, 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        onChange();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      events?.close();
      window.clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Unread count in the tab title, as mail clients do.
  useEffect(() => {
    const unread = folders.find((f) => f.specialUse === '\\Inbox')?.unseen ?? 0;
    document.title = unread > 0 ? `(${unread}) ZitiMail` : 'ZitiMail';
  }, [folders]);

  // ─── Acting on messages ──────────────────────────────────────────────────

  const uidsOf = (list: readonly Conversation[]) =>
    list.flatMap((r) => r.messages.map((m) => m.uid));
  const keysOf = (list: readonly Conversation[]) =>
    new Set(list.flatMap((r) => r.messages.map(rowKey)));

  /**
   * Runs one action on whole conversations. Moves leave the list at once and
   * come with Undo; flag and read changes show at once. The server's answer
   * reloads the list either way.
   */
  /** Trash or Junk, all of it, after asking; still recoverable for a while. */
  const emptyFolder = async (f: Folder) => {
    const what = f.specialUse === '\\Junk' ? 'Junk' : 'Trash';
    const n = f.total;
    if (
      !window.confirm(
        `Empty ${what}? ${n === 1 ? 'Its message is' : `All ${n} messages in it are`} deleted. You can still recover them for a while.`,
      )
    ) {
      return;
    }
    setMessages([]);
    setOpenKey(null);
    try {
      const { deleted } = await mail.emptyFolder(f.path);
      toast(
        `${what} emptied: ${deleted} ${deleted === 1 ? 'message' : 'messages'} deleted.`,
        onRecover ? { action: { label: 'Recover', run: onRecover } } : undefined,
      );
    } catch (e) {
      toast(e instanceof Error ? e.message : `${what} was not emptied.`, { tone: 'error' });
    } finally {
      refresh.current();
    }
  };

  const act = async (action: BulkAction, list: readonly Conversation[], to?: string) => {
    if (list.length === 0) {
      return;
    }
    const uids = uidsOf(list);
    const leaving = ['archive', 'junk', 'delete', 'move'].includes(action);
    const gone = keysOf(list);
    if (leaving) {
      setMessages((all) => all.filter((m) => !gone.has(rowKey(m))));
      if (open && gone.has(rowKey(open.latest))) {
        setOpenKey(null);
      }
    } else {
      const patch: Partial<MessageSummary> =
        action === 'seen'
          ? { seen: true }
          : action === 'unseen'
            ? { seen: false }
            : { flagged: action === 'flag' };
      setMessages((all) => all.map((m) => (gone.has(rowKey(m)) ? { ...m, ...patch } : m)));
    }
    setSelected(new Set());
    try {
      const result = await mail.bulk({ folder, uids, action, ...(to ? { to } : {}) });
      const n = list.length;
      const what = n === 1 ? 'Conversation' : `${n} conversations`;
      const verb = {
        archive: 'archived',
        junk: 'moved to Junk',
        delete: 'moved to Trash',
        move: 'moved',
      }[action as string];
      if (result.deleted) {
        // Deleted from Trash or Junk: still recoverable for a while
        // (lazy_expunge, ADR-0032), so say where rather than ask first.
        toast(
          `${what} deleted from ${folder === 'INBOX' ? 'Inbox' : folder}. You can still recover it for a while.`,
          onRecover ? { action: { label: 'Recover', run: onRecover } } : undefined,
        );
      } else if (verb && result.moved?.uids.length) {
        const back = result.moved;
        toast(`${what} ${verb}.`, {
          action: {
            label: 'Undo',
            run: () =>
              void mail
                .bulk({ folder: back.folder, uids: back.uids, action: 'move', to: folder })
                .then(() => {
                  refresh.current();
                  toast('Undone.');
                })
                .catch((e: unknown) =>
                  toast(e instanceof Error ? e.message : 'Not undone.', { tone: 'error' }),
                ),
          },
        });
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That did not work.', { tone: 'error' });
    } finally {
      refresh.current();
    }
  };

  const rowAction = (row: Conversation, action: RowAction) => {
    if (action === 'archive' || action === 'delete') {
      void act(action, [row]);
    } else if (action === 'toggle-read') {
      void act(row.unread ? 'seen' : 'unseen', [row]);
    } else {
      void act(row.flagged ? 'unflag' : 'flag', [row]);
    }
  };

  const conversationAction = (action: ConversationAction, row: Conversation) => {
    switch (action) {
      case 'not-junk':
        void act('move', [row], 'INBOX');
        break;
      case 'unread':
        void act('unseen', [row]);
        setOpenKey(null);
        break;
      default:
        void act(action, [row]);
    }
  };

  // ─── Drag and drop, right-click ──────────────────────────────────────────

  /** What a drag or a menu acts on: the selection when `row` is in it. */
  const rowsFor = (row: Conversation): readonly Conversation[] =>
    selected.has(rowKey(row.latest)) && selectedRows.length > 1 ? selectedRows : [row];

  /** Whether messages may be dropped into `f`: not where they are, not the app's folders. */
  const accepts = (f: Folder): boolean =>
    f.path !== folder &&
    f.specialUse !== '\\Drafts' &&
    f.path !== 'Scheduled' &&
    f.path !== 'Snoozed';

  /** Into Trash, Junk or Archive is that action (Junk teaches the filter); elsewhere, a move. */
  const dropInto = (f: Folder, list: readonly Conversation[]) => {
    if (f.specialUse === '\\Trash') {
      void act('delete', list);
    } else if (f.specialUse === '\\Junk') {
      void act('junk', list);
    } else if (f.specialUse === '\\Archive') {
      void act('archive', list);
    } else {
      void act('move', list, f.path);
    }
  };

  const replyTo = (row: Conversation, mode: ReplyMode) => {
    void mail
      .message(row.latest.folder, row.latest.uid)
      .then((detail) => reply(detail, mode))
      .catch(fail);
  };

  const messageMenu = (row: Conversation, x: number, y: number) => {
    const list = rowsFor(row);
    const one = list.length === 1;
    if (one) {
      setFocusKey(rowKey(row.latest));
    }
    const unread = list.some((r) => r.unread);
    const flagged = list.every((r) => r.flagged);
    const inJunk = folder === byUse.get('\\Junk');
    const targets = folders.filter(accepts);
    const items: MenuItem[] = [
      ...(one
        ? [
            { label: 'Open', onSelect: () => openRow(row) },
            {
              label: selected.has(rowKey(row.latest)) ? 'Deselect' : 'Select',
              onSelect: () => toggle(row, false),
            },
            { label: 'Reply', onSelect: () => replyTo(row, 'reply'), separator: true },
            { label: 'Reply all', onSelect: () => replyTo(row, 'all') },
            { label: 'Forward', onSelect: () => replyTo(row, 'forward') },
          ]
        : []),
      {
        label: unread ? 'Mark as read' : 'Mark as unread',
        onSelect: () => void act(unread ? 'seen' : 'unseen', list),
        separator: one,
      },
      {
        label: flagged ? 'Unflag' : 'Flag',
        onSelect: () => void act(flagged ? 'unflag' : 'flag', list),
      },
      {
        label: 'Move to',
        separator: true,
        disabled: targets.length === 0,
        items: targets.map((f) => ({
          label: folderLabel(f, folders),
          onSelect: () => dropInto(f, list),
        })),
      },
      { label: 'Archive', onSelect: () => void act('archive', list) },
      inJunk
        ? {
            label: 'Not junk',
            onSelect: () => void act('move', list, byUse.get('\\Inbox') ?? 'INBOX'),
          }
        : { label: 'Junk', onSelect: () => void act('junk', list) },
      { label: 'Delete', danger: true, onSelect: () => void act('delete', list) },
    ];
    setMenu({ x, y, label: one ? 'Message' : `${list.length} conversations`, items });
  };

  const folderMenu = (f: Folder, x: number, y: number) => {
    const go = () => {
      setSearch('');
      setQuery('');
      setFolder(f.path);
    };
    const start = (mode: 'rename' | 'move') => {
      go();
      setFolderStart({ path: f.path, mode, n: Date.now() });
    };
    const own = canChangeFolder(f);
    const items: MenuItem[] = [
      { label: 'Open', onSelect: go },
      ...(own
        ? [
            { label: 'Rename', onSelect: () => start('rename'), separator: true },
            {
              label: 'Move',
              disabled:
                folderMoveTargets(f, folders).length === 0 && !f.path.includes(FOLDER_SEPARATOR),
              onSelect: () => start('move'),
            },
            {
              label: 'Delete',
              danger: true,
              onSelect: () => {
                if (!askToDeleteFolder(f)) {
                  return;
                }
                void mail
                  .deleteFolder(f.path)
                  .then(({ movedToTrash: moved }) => {
                    toast(
                      moved
                        ? `Folder deleted. Its ${moved} ${moved === 1 ? 'message is' : 'messages are'} in Trash.`
                        : 'Folder deleted.',
                    );
                    if (folder === f.path || folder.startsWith(f.path + FOLDER_SEPARATOR)) {
                      setFolder(byUse.get('\\Inbox') ?? 'INBOX');
                    }
                    loadFolders();
                  })
                  .catch(fail);
              },
            },
          ]
        : []),
    ];
    setMenu({ x, y, label: `Folder ${f.name}`, items });
  };

  const openRow = (row: Conversation) => {
    // A draft opens where it is finished: in the compose window.
    if (byUse.get('\\Drafts') === folder) {
      const m = row.latest;
      void Promise.all([mail.message(m.folder, m.uid), mail.draftAttachments(m.uid)])
        .then(([d, files]) =>
          setDraft({
            to: d.to.map((a) => a.address).join(', '),
            cc: d.cc.map((a) => a.address).join(', '),
            subject: d.subject,
            text: '',
            html: d.html ?? textToHtml(d.text ?? ''),
            draftUid: d.uid,
            ...draftFiles(files),
          }),
        )
        .catch(fail);
      return;
    }
    setOpenKey(rowKey(row.latest));
    setFocusKey(rowKey(row.latest));
    if (row.unread) {
      // The reader marks what it opens as read; show it in the list now.
      const keys = keysOf([row]);
      setMessages((all) => all.map((m) => (keys.has(rowKey(m)) ? { ...m, seen: true } : m)));
      window.setTimeout(loadFolders, 800);
    }
  };

  const toggle = (row: Conversation, range: boolean) => {
    const key = rowKey(row.latest);
    setSelected((current) => {
      const next = new Set(current);
      // A range runs from the last row selected, or else the one open.
      const from = anchor ?? focusKey ?? openKey;
      if (range && from) {
        const a = rows.findIndex((r) => rowKey(r.latest) === from);
        const b = rows.findIndex((r) => rowKey(r.latest) === key);
        if (a >= 0 && b >= 0) {
          for (const r of rows.slice(Math.min(a, b), Math.max(a, b) + 1)) {
            next.add(rowKey(r.latest));
          }
          return next;
        }
      }
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    setAnchor(key);
  };

  const selectedRows = rows.filter((r) => selected.has(rowKey(r.latest)));
  const selectAll = () => setSelected(new Set(rows.map((r) => rowKey(r.latest))));

  const reply = (detail: MessageDetail, mode: ReplyMode) => {
    // The original, quoted as it was written: its sanitised HTML when it has
    // one, otherwise its text.
    const original = detail.html ?? textToHtml(detail.text ?? '');
    const header = `On ${fullDate(detail.date)}, ${displayName(detail.from)} &lt;${detail.from.address}&gt; wrote:`;
    if (mode === 'forward') {
      // The original's files go with it, as in Outlook; each can be removed.
      void forwardedFiles(detail).then(({ files, left }) => {
        if (left > 0) {
          toast(
            `${left} attachment${left === 1 ? ' was' : 's were'} left out: forwarded files are limited to 25 MB together.`,
            { tone: 'error' },
          );
        }
        setDraft({
          to: '',
          cc: '',
          subject: /^fwd?:/i.test(detail.subject) ? detail.subject : `Fwd: ${detail.subject}`,
          text: '',
          html: `<p></p>${signatureHtml(signature)}<p>---------- Forwarded message ----------<br>From: ${detail.from.address}<br>Date: ${fullDate(detail.date)}<br>Subject: ${detail.subject.replace(/</g, '&lt;')}</p>${original}`,
          ...(files.length ? { attachments: files } : {}),
        });
      });
      return;
    }
    const to = [(detail.replyTo[0] ?? detail.from).address];
    const cc =
      mode === 'all'
        ? [...detail.to, ...detail.cc]
            .map((a) => a.address)
            .filter((a) => a.toLowerCase() !== email.toLowerCase() && !to.includes(a))
        : [];
    setDraft({
      to: to.join(', '),
      cc: cc.join(', '),
      subject: /^re:/i.test(detail.subject) ? detail.subject : `Re: ${detail.subject}`,
      text: '',
      html: `<p></p>${signatureHtml(signature)}<p>${header}</p><blockquote>${original}</blockquote>`,
      original: {
        from: detail.from.name
          ? `${detail.from.name} <${detail.from.address}>`
          : detail.from.address,
        subject: detail.subject,
        body: plainBody(detail),
        ...(detail.aiVerdict ? { verdict: detail.aiVerdict.verdict } : {}),
      },
      ...(detail.messageId
        ? {
            inReplyTo: detail.messageId,
            references: [...(detail.references ?? []), detail.messageId],
          }
        : {}),
    });
  };

  // ─── Keyboard ────────────────────────────────────────────────────────────

  const keys = useRef<(e: KeyboardEvent) => void>(() => undefined);
  keys.current = (e: KeyboardEvent) => {
    if (typing(e.target) || draft) {
      return;
    }
    // Ctrl-A (Cmd-A): every message in the list, not the page's text.
    if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'a' && rows.length) {
      e.preventDefault();
      selectAll();
      return;
    }
    if (e.metaKey || e.ctrlKey || e.altKey) {
      return;
    }
    // The divider's arrow keys resize; they do not move through the list.
    if (e.target instanceof Element && e.target.closest('[role="separator"]')) {
      return;
    }
    const index = rows.findIndex((r) => rowKey(r.latest) === (focusKey ?? openKey));
    const current = index >= 0 ? rows[index] : open;
    const step = (d: number) => {
      const next = rows[Math.max(0, Math.min(rows.length - 1, index + d))];
      if (next) {
        setFocusKey(rowKey(next.latest));
        if (openKey) {
          openRow(next);
        }
        document.querySelector('.msg-row.focus')?.scrollIntoView({ block: 'nearest' });
      }
    };
    const handled = () => e.preventDefault();
    if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
      // A folder's own menu key is the folder's (its button handles it).
      const row = document.querySelector('.msg-row.focus, .msg-row.open');
      if (current && row && !(e.target instanceof Element && e.target.closest('.folders'))) {
        handled();
        const at = menuAtElement(row, '', []);
        messageMenu(current, at.x, at.y);
      }
      return;
    }
    switch (e.key) {
      case 'j':
      case 'ArrowDown':
        handled();
        step(1);
        break;
      case 'k':
      case 'ArrowUp':
        handled();
        step(-1);
        break;
      case 'Enter':
      case 'o':
        if (current) {
          handled();
          openRow(current);
        }
        break;
      case 'Escape':
        setOpenKey(null);
        setSelected(new Set());
        break;
      case 'e':
        if (selectedRows.length || current) {
          handled();
          void act('archive', selectedRows.length ? selectedRows : [current as Conversation]);
        }
        break;
      case '#':
      case 'Delete':
        if (selectedRows.length || current) {
          handled();
          void act('delete', selectedRows.length ? selectedRows : [current as Conversation]);
        }
        break;
      case 's':
        if (current) {
          handled();
          void act(current.flagged ? 'unflag' : 'flag', [current]);
        }
        break;
      case 'u':
        if (current) {
          handled();
          void act('unseen', [current]);
          setOpenKey(null);
        }
        break;
      case 'x':
        if (current) {
          handled();
          toggle(current, false);
        }
        break;
      case 'c':
        handled();
        setDraft({ to: '', cc: '', subject: '', text: '' });
        break;
      case '/':
        handled();
        searchBox.current?.focus();
        break;
      case '?':
        handled();
        setHelp(true);
        break;
      case 'r':
      case 'a':
      case 'f': {
        const detailButton = document.querySelector<HTMLButtonElement>(
          `.msg-card:last-of-type .card-actions button[aria-label="${
            { r: 'Reply', a: 'Reply all', f: 'Forward' }[e.key]
          }"]`,
        );
        if (detailButton) {
          handled();
          detailButton.click();
        }
        break;
      }
    }
  };
  useEffect(() => {
    const listener = (e: KeyboardEvent) => keys.current(e);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);

  // ─── Layout ──────────────────────────────────────────────────────────────

  return (
    <>
      <ContextMenu at={menu} onClose={closeMenu} />
      <nav className={`folders${foldersOpen ? ' open' : ''}`} aria-label="Folders">
        <button
          type="button"
          className="compose"
          onClick={() => {
            setFoldersOpen(false);
            setDraft({ to: '', cc: '', subject: '', text: '' });
          }}
          title="New message (c)"
        >
          <PenIcon /> New message
        </button>
        {folders.map((f, i) => {
          const Icon =
            FOLDER_ICON[f.specialUse ?? (f.shared && f.name === 'Inbox' ? '\\Inbox' : '')] ??
            (f.path === 'Scheduled' || f.path === 'Snoozed' ? ClockIcon : FolderMoveIcon);
          // Another mailbox this person was given (ADR-0047): its address
          // heads its folders.
          const heading = f.shared && f.shared !== folders[i - 1]?.shared && (
            <div className="folder-group" title={`Shared mailbox ${f.shared}`}>
              {f.shared}
            </div>
          );
          const depth = folderDepth(f);
          return (
            <Fragment key={f.path}>
              {heading}
              <button
                type="button"
                className={`${f.path === folder ? 'folder on' : 'folder'}${f.shared ? ' shared' : ''}${depth ? ' nested' : ''}`}
                style={depth ? ({ '--depth': depth } as CSSProperties) : undefined}
                aria-current={f.path === folder ? 'true' : undefined}
                data-drop={dropOn === f.path ? 'on' : undefined}
                onClick={() => {
                  setSearch('');
                  setQuery('');
                  setFolder(f.path);
                  setFoldersOpen(false);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  folderMenu(f, e.clientX, e.clientY);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
                    e.preventDefault();
                    const at = menuAtElement(e.currentTarget, '', []);
                    folderMenu(f, at.x, at.y);
                  }
                }}
                onDragOver={(e) => {
                  if (!e.dataTransfer.types.includes(MESSAGES_DRAG) || !accepts(f)) {
                    return;
                  }
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dropOn !== f.path) {
                    setDropOn(f.path);
                  }
                }}
                onDragLeave={() => setDropOn((on) => (on === f.path ? null : on))}
                onDrop={(e) => {
                  e.preventDefault();
                  setDropOn(null);
                  if (accepts(f) && dragged.current.length) {
                    dropInto(f, dragged.current);
                  }
                  dragged.current = [];
                }}
              >
                <Icon />
                <span className="folder-name">{f.name}</span>
                {f.unseen > 0 && f.specialUse !== '\\Sent' && (
                  <span className="count">{f.unseen}</span>
                )}
              </button>
            </Fragment>
          );
        })}
        <NewFolder
          folders={folders}
          onCreated={(name) => {
            toast(`Folder ${name} created.`);
            loadFolders();
          }}
        />
        {current && canChangeFolder(current) && (
          <FolderTools
            key={`${current.path}:${folderStart?.path === current.path ? folderStart.n : 0}`}
            start={folderStart?.path === current.path ? folderStart.mode : undefined}
            folder={current}
            folders={folders}
            onRenamed={(path, rules) => {
              toast(
                rules
                  ? `Folder renamed. ${rules} ${rules === 1 ? 'rule files' : 'rules file'} into the new name.`
                  : 'Folder renamed.',
              );
              setFolder(path);
              loadFolders();
            }}
            onMoved={(path, rules) => {
              toast(
                rules
                  ? `Folder moved. ${rules} ${rules === 1 ? 'rule files' : 'rules file'} into its new place.`
                  : 'Folder moved.',
              );
              setFolder(path);
              loadFolders();
            }}
            onDeleted={(moved) => {
              toast(
                moved
                  ? `Folder deleted. Its ${moved} ${moved === 1 ? 'message is' : 'messages are'} in Trash.`
                  : 'Folder deleted.',
              );
              setFolder(byUse.get('\\Inbox') ?? 'INBOX');
              loadFolders();
            }}
          />
        )}
        <span className="spacer" />
        <button type="button" className="folder" onClick={onSettings}>
          Settings
        </button>
        {onRecover && (
          <button type="button" className="folder" onClick={onRecover}>
            Recover deleted mail
          </button>
        )}
        <button type="button" className="folder" onClick={() => setHelp(true)}>
          Keyboard shortcuts
        </button>
        {quota && quota.limitBytes > 0 && (
          <div
            className={`quota ${quotaLevel(quota)}`}
            title={`${(quota.usedBytes / 1024 ** 2).toFixed(0)} MB of ${(quota.limitBytes / 1024 ** 3).toFixed(1)} GB`}
          >
            <div className="bar">
              <span style={{ width: `${Math.min(100, Math.round(quotaRatio(quota) * 100))}%` }} />
            </div>
            {Math.round(quotaRatio(quota) * 100)}% of your mailbox used
            {quotaLevel(quota) === 'full' && (
              <div className="quota-note">
                New mail is being returned to senders. Empty Trash and Junk to make room.
              </div>
            )}
            {quotaLevel(quota) === 'near' && (
              <div className="quota-note">Nearly full: empty Trash and Junk to make room.</div>
            )}
          </div>
        )}
        <BuildInfo product="ZitiMail" />
      </nav>
      {foldersOpen && (
        <button
          type="button"
          className="drawer-scrim"
          aria-label="Close folders"
          onClick={() => setFoldersOpen(false)}
        />
      )}

      <main
        className={`main mail-main pane-${layout.pane}${open ? ' reading' : ''}`}
        style={
          {
            '--list-w': `${layout.listWidth}px`,
            '--list-h': `${layout.listHeight}px`,
          } as CSSProperties
        }
      >
        <button
          type="button"
          className="compose-fab mobile-only"
          aria-label="New message"
          onClick={() => setDraft({ to: '', cc: '', subject: '', text: '' })}
        >
          <PenIcon />
        </button>
        <section className="mail-list" aria-label={folderName}>
          {selectedRows.length > 0 ? (
            <div className="list-head bulk">
              <button
                type="button"
                className="icon-btn"
                title="Clear selection"
                aria-label="Clear selection"
                onClick={() => setSelected(new Set())}
              >
                <CloseIcon />
              </button>
              <span className="bulk-count">{selectedRows.length} selected</span>
              {selectedRows.length < rows.length && (
                <button type="button" className="link-btn" onClick={selectAll}>
                  Select all {rows.length}
                </button>
              )}
              <button
                type="button"
                className="icon-btn"
                title="Archive"
                aria-label="Archive selected"
                onClick={() => void act('archive', selectedRows)}
              >
                <ArchiveIcon />
              </button>
              <button
                type="button"
                className="icon-btn"
                title="Delete"
                aria-label="Delete selected"
                onClick={() => void act('delete', selectedRows)}
              >
                <TrashIcon />
              </button>
              <button
                type="button"
                className="icon-btn"
                title="Junk"
                aria-label="Move selected to Junk"
                onClick={() => void act('junk', selectedRows)}
              >
                <ShieldIcon />
              </button>
              <button
                type="button"
                className="icon-btn"
                title="Mark read"
                aria-label="Mark selected read"
                onClick={() => void act('seen', selectedRows)}
              >
                <EnvelopeOpenIcon />
              </button>
              <button
                type="button"
                className="icon-btn"
                title="Flag"
                aria-label="Flag selected"
                onClick={() => void act('flag', selectedRows)}
              >
                <FlagIcon />
              </button>
              <select
                aria-label="Move selected to folder"
                value=""
                onChange={(e) => e.target.value && void act('move', selectedRows, e.target.value)}
              >
                <option value="">Move to…</option>
                {folders
                  .filter((f) => f.path !== folder)
                  .map((f) => (
                    <option key={f.path} value={f.path}>
                      {folderLabel(f, folders)}
                    </option>
                  ))}
              </select>
            </div>
          ) : (
            <div className="list-head">
              <button
                type="button"
                className="icon-btn mobile-only"
                title="Folders"
                aria-label="Folders"
                onClick={() => setFoldersOpen(true)}
              >
                <MenuIcon />
              </button>
              <form
                className="search-field"
                role="search"
                onSubmit={(e) => {
                  e.preventDefault();
                  setQuery(search.trim());
                }}
              >
                <SearchIcon />
                <input
                  ref={searchBox}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={`Search ${folderName}`}
                  aria-label="Search mail"
                  title="from:  to:  subject:  has:attachment  is:unread  is:flagged  after:2026-01-31  before:  larger:5M  -word to exclude"
                />
                {query && (
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label="Clear search"
                    onClick={() => {
                      setSearch('');
                      setQuery('');
                    }}
                  >
                    <CloseIcon />
                  </button>
                )}
              </form>
              <button
                type="button"
                className="icon-btn"
                title="Refresh"
                aria-label="Refresh"
                onClick={() => refresh.current()}
              >
                <RefreshIcon className={busy ? 'spin' : ''} />
              </button>
            </div>
          )}
          <div className="list-sub">
            <span>
              {query ? `${total} found` : `${folderName}`}
              {!query && byUse.get('\\Inbox') === folder && folders[0] && folders[0].unseen > 0
                ? ` · ${folders[0].unseen} unread`
                : ''}
            </span>
            {!query &&
              current &&
              !current.shared &&
              (current.specialUse === '\\Trash' || current.specialUse === '\\Junk') &&
              current.total > 0 && (
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => void emptyFolder(current)}
                >
                  Empty {current.specialUse === '\\Junk' ? 'Junk' : 'Trash'}
                </button>
              )}
            <label className="threads-toggle">
              <input
                type="checkbox"
                checked={threads}
                onChange={(e) => {
                  setThreads(e.target.checked);
                  try {
                    window.localStorage.setItem(THREADS_KEY, e.target.checked ? 'on' : 'off');
                  } catch {
                    // Not remembered; the choice still holds for this visit.
                  }
                }}
              />
              Conversations
            </label>
          </div>
          {error && (
            <div className="alert" role="alert">
              {error}
            </div>
          )}
          {!loaded ? (
            <ListSkeleton />
          ) : rows.length === 0 ? (
            <div className="list-empty">
              {query ? (
                <p>
                  Nothing in {folderName} matches “{query}”.
                </p>
              ) : (
                <p>{folderName} is empty.</p>
              )}
            </div>
          ) : (
            <MessageList
              rows={rows}
              openKey={open ? rowKey(open.latest) : null}
              focusKey={focusKey}
              selected={selected}
              me={email}
              onOpen={openRow}
              onToggle={toggle}
              onAction={rowAction}
              onDragRows={(row) => {
                dragged.current = rowsFor(row);
                return dragged.current;
              }}
              onMenu={(row, at) => messageMenu(row, at.x, at.y)}
            />
          )}
          {cursor && (
            <button
              type="button"
              className="load-more"
              disabled={busy}
              onClick={() => loadMessages(true)}
            >
              Show older messages ({total - messages.length} more)
            </button>
          )}
        </section>

        {layout.pane !== 'off' && (
          <Splitter
            direction={layout.pane}
            size={layout.pane === 'right' ? layout.listWidth : layout.listHeight}
          />
        )}

        {open ? (
          <Reader
            tenant={tenant}
            conversation={open}
            folders={folders}
            folder={folder}
            me={email}
            onAction={(a) => conversationAction(a, open)}
            onMove={(to) => void act('move', [open], to)}
            onReply={reply}
            onClose={() => setOpenKey(null)}
            onMakeRule={onMakeRule}
            onSnooze={
              isSnoozedFolder || isScheduledFolder || byUse.get('\\Drafts') === folder
                ? undefined
                : (until) => void snooze(open, until)
            }
            banner={(() => {
              if (isSnoozedFolder) {
                const nap = snoozed.find((j) => j.uid === open.latest.uid);
                return nap ? (
                  <div className={`verdict ${nap.state === 'failed' ? 'warn' : 'info'}`}>
                    <ClockIcon className="verdict-icon" />
                    <span>
                      {nap.state === 'failed' ? (
                        <strong>Could not be brought back on time.</strong>
                      ) : (
                        <>
                          <strong>Back in your Inbox {whenLabel(new Date(nap.until))}.</strong>
                        </>
                      )}
                    </span>
                    <button
                      type="button"
                      className="text-btn"
                      onClick={() =>
                        void mail
                          .wakeSnoozed(nap.id)
                          .then(() => {
                            toast('Back in your Inbox.');
                            setOpenKey(null);
                            refresh.current();
                          })
                          .catch((e: unknown) =>
                            toast(e instanceof Error ? e.message : 'Not moved.', { tone: 'error' }),
                          )
                      }
                    >
                      Back to Inbox now
                    </button>
                  </div>
                ) : null;
              }
              if (!isScheduledFolder) {
                return null;
              }
              const job = scheduled.find((j) => j.uid === open.latest.uid);
              if (!job) {
                return null;
              }
              const when = new Date(job.sendAt).toLocaleString([], {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                hour: '2-digit',
                minute: '2-digit',
              });
              return (
                <div className={`verdict ${job.state === 'failed' ? 'warn' : 'info'}`}>
                  <ClockIcon className="verdict-icon" />
                  <span>
                    {job.state === 'failed' ? (
                      <>
                        <strong>Not sent.</strong> {job.error ?? 'Sending failed.'}
                      </>
                    ) : (
                      <>
                        <strong>Sends {when}.</strong> To {job.to.join(', ')}.
                      </>
                    )}
                  </span>
                  <button
                    type="button"
                    className="text-btn"
                    onClick={() =>
                      void mail
                        .cancelScheduled(job.id)
                        .then(async (back) => {
                          toast('Sending cancelled; the message is in Drafts.');
                          setOpenKey(null);
                          refresh.current();
                          if (back.draftUid) {
                            const [d, files] = await Promise.all([
                              mail.message(byUse.get('\\Drafts') ?? 'Drafts', back.draftUid),
                              mail.draftAttachments(back.draftUid),
                            ]);
                            setDraft({
                              to: d.to.map((a) => a.address).join(', '),
                              cc: d.cc.map((a) => a.address).join(', '),
                              subject: d.subject,
                              text: '',
                              html: d.html ?? textToHtml(d.text ?? ''),
                              draftUid: d.uid,
                              ...draftFiles(files),
                            });
                          }
                        })
                        .catch((e: unknown) =>
                          toast(e instanceof Error ? e.message : 'Not cancelled.', {
                            tone: 'error',
                          }),
                        )
                    }
                  >
                    {job.state === 'failed' ? 'Edit in Drafts' : 'Cancel sending'}
                  </button>
                </div>
              );
            })()}
          />
        ) : (
          <section className="reader empty-reader" aria-label="Message">
            <div className="empty">
              <div className="icon" aria-hidden="true">
                <InboxIcon />
              </div>
              <h3>No message open</h3>
              <p className="muted">
                Choose a conversation, or press <kbd>?</kbd> for keyboard shortcuts.
              </p>
            </div>
          </section>
        )}
      </main>

      {draft && (
        <Compose
          key={draft.draftUid ?? `${draft.subject}:${draft.to}`}
          draft={draft}
          from={email}
          signature={signature}
          plain={plainWriting}
          tenant={tenant}
          senderName={name}
          onClose={() => {
            setDraft(null);
            refresh.current();
          }}
          onSend={sendLater}
          onSchedule={(request, at, reopen) => void scheduleLater(request, at, reopen)}
        />
      )}
      {help && <Shortcuts onClose={() => setHelp(false)} />}
    </>
  );
}

/**
 * Makes a folder of the person's own, at the top or inside another. It is
 * made on the server, so Outlook, phones and the desktop app show it too.
 */
export function NewFolder({
  folders,
  onCreated,
}: {
  folders: readonly Folder[];
  onCreated: (name: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [parent, setParent] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const close = () => {
    setOpen(false);
    setName('');
    setParent('');
    setProblem(null);
  };
  if (!open) {
    return (
      <button type="button" className="folder new-folder" onClick={() => setOpen(true)}>
        <PlusIcon />
        <span className="folder-name">New folder</span>
      </button>
    );
  }
  const create = async () => {
    const made = newFolderPath(name, parent || undefined, folders);
    if ('problem' in made) {
      setProblem(made.problem);
      return;
    }
    setBusy(true);
    try {
      await mail.createFolder(name.trim(), parent || undefined);
      onCreated(name.trim());
      close();
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'The folder was not created. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="new-folder-form"
      aria-label="New folder"
      onSubmit={(e) => {
        e.preventDefault();
        void create();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          close();
        }
      }}
    >
      <input
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setProblem(null);
        }}
        placeholder="Folder name"
        aria-label="Folder name"
        maxLength={100}
        autoFocus
      />
      <select value={parent} onChange={(e) => setParent(e.target.value)} aria-label="Put it in">
        <option value="">At the top</option>
        {folders.filter(canHoldFolders).map((f) => (
          <option key={f.path} value={f.path}>
            Inside {folderLabel(f, folders)}
          </option>
        ))}
      </select>
      {problem && (
        <p className="new-folder-problem" role="alert">
          {problem}
        </p>
      )}
      <span className="new-folder-actions">
        <button type="submit" className="btn" disabled={busy || !name.trim()}>
          Create
        </button>
        <button type="button" className="btn ghost" onClick={close}>
          Cancel
        </button>
      </span>
    </form>
  );
}

/**
 * Rename or delete the open folder, when it is one the person made. A
 * deleted folder's mail goes to Trash; the server refuses while folders are
 * inside it or a rule files into it, and says so.
 */
/** Asks before deleting `folder`, saying where its mail goes. */
export function askToDeleteFolder(folder: Folder): boolean {
  return window.confirm(
    folder.total
      ? `Delete the folder ${folder.name}? Its ${folder.total} ${folder.total === 1 ? 'message goes' : 'messages go'} to Trash.`
      : `Delete the folder ${folder.name}?`,
  );
}

export function FolderTools({
  folder,
  folders,
  start,
  onRenamed,
  onMoved,
  onDeleted,
}: {
  folder: Folder;
  folders: readonly Folder[];
  /** Open straight on renaming or moving (asked for from the folder's menu). */
  start?: 'rename' | 'move' | undefined;
  onRenamed: (path: string, rulesUpdated: number) => void;
  onMoved: (path: string, rulesUpdated: number) => void;
  onDeleted: (movedToTrash: number) => void;
}): JSX.Element {
  const targets = folderMoveTargets(folder, folders);
  const atTop = !folder.path.includes(FOLDER_SEPARATOR);
  const [renaming, setRenaming] = useState(start === 'rename');
  const [moving, setMoving] = useState(start === 'move');
  const [into, setInto] = useState(start === 'move' && atTop ? (targets[0]?.path ?? '') : '');
  const [name, setName] = useState(folder.name);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const failed = (e: unknown) =>
    setProblem(e instanceof Error ? e.message : 'The mail server did not answer. Try again.');
  const rename = async () => {
    const made = renamedFolderPath(folder.path, name, folders);
    if ('problem' in made) {
      setProblem(made.problem);
      return;
    }
    setBusy(true);
    try {
      const done = await mail.renameFolder(folder.path, name.trim());
      setRenaming(false);
      onRenamed(done.path, done.rulesUpdated);
    } catch (e) {
      failed(e);
    } finally {
      setBusy(false);
    }
  };
  const move = async () => {
    const made = movedFolderPath(folder.path, into || undefined, folders);
    if ('problem' in made) {
      setProblem(made.problem);
      return;
    }
    setBusy(true);
    try {
      const done = await mail.moveFolder(folder.path, into || undefined);
      setMoving(false);
      onMoved(done.path, done.rulesUpdated);
    } catch (e) {
      failed(e);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!askToDeleteFolder(folder)) {
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      onDeleted((await mail.deleteFolder(folder.path)).movedToTrash);
    } catch (e) {
      failed(e);
    } finally {
      setBusy(false);
    }
  };
  if (moving) {
    return (
      <form
        className="new-folder-form"
        aria-label="Move folder"
        onSubmit={(e) => {
          e.preventDefault();
          void move();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setMoving(false);
            setProblem(null);
          }
        }}
      >
        <select
          value={into}
          onChange={(e) => {
            setInto(e.target.value);
            setProblem(null);
          }}
          aria-label="Move it to"
          autoFocus
        >
          {!atTop && <option value="">To the top</option>}
          {targets.map((f) => (
            <option key={f.path} value={f.path}>
              Inside {folderLabel(f, folders)}
            </option>
          ))}
        </select>
        {problem && (
          <p className="new-folder-problem" role="alert">
            {problem}
          </p>
        )}
        <span className="new-folder-actions">
          <button type="submit" className="btn" disabled={busy}>
            Move
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() => {
              setMoving(false);
              setProblem(null);
            }}
          >
            Cancel
          </button>
        </span>
      </form>
    );
  }
  if (renaming) {
    return (
      <form
        className="new-folder-form"
        aria-label="Rename folder"
        onSubmit={(e) => {
          e.preventDefault();
          void rename();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setRenaming(false);
            setProblem(null);
          }
        }}
      >
        <input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setProblem(null);
          }}
          aria-label="New name"
          maxLength={100}
          autoFocus
        />
        {problem && (
          <p className="new-folder-problem" role="alert">
            {problem}
          </p>
        )}
        <span className="new-folder-actions">
          <button type="submit" className="btn" disabled={busy || !name.trim()}>
            Rename
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() => {
              setRenaming(false);
              setProblem(null);
            }}
          >
            Cancel
          </button>
        </span>
      </form>
    );
  }
  return (
    <>
      <button
        type="button"
        className="folder new-folder"
        onClick={() => {
          setName(folder.name);
          setRenaming(true);
        }}
      >
        <PenIcon />
        <span className="folder-name">Rename this folder</span>
      </button>
      {(targets.length > 0 || !atTop) && (
        <button
          type="button"
          className="folder new-folder"
          onClick={() => {
            setInto(atTop ? (targets[0]?.path ?? '') : '');
            setProblem(null);
            setMoving(true);
          }}
        >
          <FolderMoveIcon />
          <span className="folder-name">Move this folder</span>
        </button>
      )}
      <button
        type="button"
        className="folder new-folder"
        disabled={busy}
        onClick={() => void remove()}
      >
        <TrashIcon />
        <span className="folder-name">Delete this folder</span>
      </button>
      {problem && (
        <p className="new-folder-problem" role="alert">
          {problem}
        </p>
      )}
    </>
  );
}
